import { useEffect, useRef, useState, type CSSProperties } from 'react'
import { COLORS } from '../lib/theme'
import { Icon } from './Icon'

export interface WaSelectOption {
  value: string
  label: string
}

export interface WaSelectProps {
  value: string
  options: WaSelectOption[]
  onChange: (value: string) => void
  ariaLabel?: string
  width?: number | string
  searchable?: boolean
}

const GAP = 6
const DESIRED_MAX_HEIGHT = 320
/** Below this much room below the trigger we prefer flipping up (if there's more room above). */
const MIN_ROOM_BELOW = 160

export interface SelectPopoverPos {
  placement: 'below' | 'above'
  top?: number
  bottom?: number
  left: number
  width: number
  maxHeight: number
}

/**
 * Anchor the menu to its trigger and open in a single direction: downward by
 * default, upward only when there isn't enough room below (and there's more
 * above). The height is clamped to the available space so a long list scrolls
 * inside the menu and never overflows / floats centered in the viewport.
 * Pure (rect + viewport in) so the placement is unit-testable.
 */
export function resolveSelectPopover(
  rect: { top: number; bottom: number; left: number; width: number },
  viewportH: number,
  viewportW: number,
): SelectPopoverPos {
  const spaceBelow = viewportH - rect.bottom - GAP
  const spaceAbove = rect.top - GAP
  const openAbove = spaceBelow < Math.min(DESIRED_MAX_HEIGHT, MIN_ROOM_BELOW) && spaceAbove > spaceBelow
  const maxHeight = Math.max(80, Math.min(DESIRED_MAX_HEIGHT, openAbove ? spaceAbove : spaceBelow))
  const left = Math.max(8, Math.min(rect.left, viewportW - rect.width - 8))
  return openAbove
    ? { placement: 'above', bottom: viewportH - rect.top + GAP, left, width: rect.width, maxHeight }
    : { placement: 'below', top: rect.bottom + GAP, left, width: rect.width, maxHeight }
}

export function WaSelect({ value, options, onChange, ariaLabel, width = 220, searchable = false }: WaSelectProps) {
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState<SelectPopoverPos | null>(null)
  const [filterText, setFilterText] = useState('')
  const [focusedIndex, setFocusedIndex] = useState<number>(-1)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([])

  const selected = options.find((o) => o.value === value)

  const filteredOptions = searchable && filterText
    ? options.filter((o) => o.label.toLowerCase().includes(filterText.toLowerCase()))
    : options

  const closePopover = () => {
    setOpen(false)
    if (searchable) {
      setFilterText('')
      setFocusedIndex(-1)
    }
  }

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closePopover()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  // Reset focused index when filtered list changes
  useEffect(() => {
    setFocusedIndex(-1)
  }, [filterText])

  // Focus search input when popover opens in searchable mode
  useEffect(() => {
    if (open && searchable && searchRef.current) {
      searchRef.current.focus()
    }
  }, [open, searchable])

  const toggle = () => {
    if (!open && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect()
      setPos(resolveSelectPopover(rect, window.innerHeight, window.innerWidth))
    }
    if (open) {
      closePopover()
    } else {
      setOpen(true)
    }
  }

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      const next = focusedIndex < filteredOptions.length - 1 ? focusedIndex + 1 : 0
      setFocusedIndex(next)
      optionRefs.current[next]?.focus()
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      const prev = focusedIndex > 0 ? focusedIndex - 1 : filteredOptions.length - 1
      setFocusedIndex(prev)
      optionRefs.current[prev]?.focus()
    } else if (e.key === 'Enter' && focusedIndex >= 0 && filteredOptions[focusedIndex]) {
      e.preventDefault()
      onChange(filteredOptions[focusedIndex].value)
      closePopover()
    } else if (e.key === 'Home') {
      e.preventDefault()
      setFocusedIndex(0)
      optionRefs.current[0]?.focus()
    } else if (e.key === 'End') {
      e.preventDefault()
      const last = filteredOptions.length - 1
      setFocusedIndex(last)
      optionRefs.current[last]?.focus()
    }
  }

  const popoverStyle: CSSProperties = pos
    ? {
        position: 'fixed',
        left: pos.left,
        width: pos.width,
        maxHeight: pos.maxHeight,
        ...(pos.placement === 'below' ? { top: pos.top } : { bottom: pos.bottom }),
      }
    : { position: 'fixed' }

  return (
    <div style={{ position: 'relative', width }}>
      <button
        ref={triggerRef}
        type="button"
        className="wh-input"
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={toggle}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          width: '100%',
          height: 36,
          padding: '0 12px',
          cursor: 'pointer',
          textAlign: 'left',
          fontSize: 13,
        }}
      >
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {selected?.label ?? ''}
        </span>
        <Icon name="chevron-down" size={14} color={COLORS.outline} style={{ transform: open ? 'rotate(180deg)' : undefined }} />
      </button>

      {open && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 1200 }} onClick={closePopover} />
          {searchable ? (
            <div
              style={{
                ...popoverStyle,
                zIndex: 1201,
                display: 'flex',
                flexDirection: 'column',
                background: COLORS.surfaceContainerLowest,
                border: `1px solid ${COLORS.surfaceContainerHighest}`,
                borderRadius: 8,
                boxShadow: '0 4px 24px rgba(0,0,0,0.13)',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ padding: '6px 6px 2px' }}>
                <input
                  ref={searchRef}
                  type="text"
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  placeholder="Search…"
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    height: 32,
                    padding: '0 10px',
                    border: `1px solid ${COLORS.surfaceContainerHighest}`,
                    borderRadius: 6,
                    fontSize: 13,
                    background: COLORS.surfaceContainerLowest,
                    color: COLORS.onSurface,
                    outline: 'none',
                  }}
                />
              </div>
              <ul
                role="listbox"
                aria-label={ariaLabel}
                style={{
                  margin: 0,
                  padding: 4,
                  listStyle: 'none',
                  overflowY: 'auto',
                  flex: 1,
                }}
              >
                {filteredOptions.map((o, idx) => {
                  const active = o.value === value
                  return (
                    <li key={o.value}>
                      <button
                        ref={(el) => { optionRefs.current[idx] = el }}
                        type="button"
                        role="option"
                        aria-selected={active}
                        onClick={() => {
                          onChange(o.value)
                          closePopover()
                        }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          width: '100%',
                          padding: '8px 10px',
                          border: 'none',
                          borderRadius: 6,
                          background: active ? COLORS.primaryContainer : 'transparent',
                          color: active ? COLORS.primary : COLORS.onSurface,
                          fontSize: 13,
                          fontWeight: active ? 700 : 500,
                          cursor: 'pointer',
                          textAlign: 'left',
                        }}
                      >
                        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {o.label}
                        </span>
                        {active && <Icon name="check" size={14} color={COLORS.primary} />}
                      </button>
                    </li>
                  )
                })}
              </ul>
            </div>
          ) : (
            <ul
              role="listbox"
              aria-label={ariaLabel}
              style={{
                ...popoverStyle,
                zIndex: 1201,
                margin: 0,
                padding: 4,
                listStyle: 'none',
                overflowY: 'auto',
                background: COLORS.surfaceContainerLowest,
                border: `1px solid ${COLORS.surfaceContainerHighest}`,
                borderRadius: 8,
                boxShadow: '0 4px 24px rgba(0,0,0,0.13)',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {options.map((o) => {
                const active = o.value === value
                return (
                  <li key={o.value}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={active}
                      onClick={() => {
                        onChange(o.value)
                        setOpen(false)
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        width: '100%',
                        padding: '8px 10px',
                        border: 'none',
                        borderRadius: 6,
                        background: active ? COLORS.primaryContainer : 'transparent',
                        color: active ? COLORS.primary : COLORS.onSurface,
                        fontSize: 13,
                        fontWeight: active ? 700 : 500,
                        cursor: 'pointer',
                        textAlign: 'left',
                      }}
                    >
                      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {o.label}
                      </span>
                      {active && <Icon name="check" size={14} color={COLORS.primary} />}
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
        </>
      )}
    </div>
  )
}
